<template>
  <div class="stock-view-page">
    <StockDocumentPageHeading
      :title="$t('STOCK.RECEIPTS.PAGE_TITLE')"
      :code="isNew ? '' : document.code"
      :new-label="isNew ? $t('STOCK.RECEIPTS.NEW') : ''"
    />
    <v-card :loading="isProcessing" :disabled="isProcessing">
      <v-tabs v-model="tab" class="stock-color" left dark>
        <v-tab href="#main">{{ document.code || $t("STOCK.RECEIPTS.NEW") }}</v-tab>
        <v-spacer></v-spacer>
        <div class="buttons-tab d-flex align-center flex-wrap pa-2">
          <v-chip v-if="document.status" class="mr-2" :color="statusColor" small dark>
            {{ statusLabel }}
          </v-chip>
          <v-btn v-if="canRegisterSupplierInvoice" class="mr-2 primary" small @click="openInvoiceLinkDialog">
            {{ invoiceLinkButtonLabel }}
          </v-btn>
          <v-btn v-if="canFinalize" class="mr-2 success" small @click="finalizeReceipt">
            {{ $t("STOCK.RECEIPTS.FINALIZE") }}
          </v-btn>
          <v-btn v-if="canConvertToPhysical" class="mr-2 primary" small @click="openConvertToPhysicalDialog">
            {{ $t("STOCK.RECEIPTS.CONVERT_TO_PHYSICAL") }}
          </v-btn>
          <v-btn v-if="canReverse" class="mr-2 warning" small @click="showReverseConfirm = true">
            {{ $t("STOCK.RECEIPTS.REVERSE") }}
          </v-btn>
          <v-btn v-if="canDiscard" class="mr-2 error" small @click="discardReceipt">
            {{ $t("STOCK.DOCUMENTS.DISCARD") }}
          </v-btn>
          <v-btn v-if="canSaveAsExpected" class="mr-2 orange darken-2" small @click="saveAsExpected">
            {{ $t("STOCK.RECEIPTS.SAVE_AS_EXPECTED") }}
          </v-btn>
          <v-btn v-if="canSave || canEditSupplierHeader" class="mr-2 success" small @click="save">
            {{ $t("GENERAL.LABELS.SAVE") }}
          </v-btn>
        </div>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item value="main">
          <v-card flat>
            <v-card-text>
              <v-alert
                v-if="isReadOnlyDocument && !canEditSupplierHeader"
                type="warning"
                dense
                outlined
                class="mb-4"
              >
                {{ $t("STOCK.RECEIPTS.READ_ONLY_DOCUMENT") }}
              </v-alert>
              <v-alert
                v-if="canEditSupplierHeader"
                type="info"
                dense
                outlined
                class="mb-4"
              >
                {{ $t("STOCK.RECEIPTS.SUPPLIER_HEADER_EDITABLE_HINT") }}
              </v-alert>
              <v-alert
                v-if="isExpectedVirtualReceipt"
                type="info"
                dense
                outlined
                class="mb-4"
              >
                {{ $t("STOCK.RECEIPTS.EXPECTED_SUPPLY_HINT") }}
              </v-alert>
              <v-alert
                v-if="showFinalizeRequiresLotsHint"
                type="warning"
                dense
                outlined
                class="mb-4"
              >
                {{ $t("STOCK.RECEIPTS.FINALIZE_REQUIRES_LOTS_HINT") }}
              </v-alert>
              <v-row>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.organization_id"
                    :items="organizations"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.WAREHOUSES.ORGANIZATION') + '*'"
                    :disabled="!canSave || document.is_finalized_editable === true"
                    :rules="canSave ? [validations.required()] : []"
                    :error-messages="validationErrors.organization_id || []"
                    @change="clearHeaderValidationError('organization_id')"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.destination_warehouse_id"
                    :items="warehouseOptions"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.RECEIPTS.WAREHOUSE') + '*'"
                    :hint="$t('STOCK.RECEIPTS.WAREHOUSE_HINT')"
                    persistent-hint
                    :disabled="!canSave || document.is_finalized_editable === true"
                    :rules="canSave ? [validations.required()] : []"
                    :error-messages="validationErrors.destination_warehouse_id || []"
                    @change="clearHeaderValidationError('destination_warehouse_id')"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.source_company_id"
                    :items="companyOptions"
                    :search-input.sync="companySearch"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.RECEIPTS.SUPPLIER')"
                    :disabled="!canEditSupplierFields"
                    :rules="canEditSupplierHeader ? [validations.required()] : []"
                    :error-messages="validationErrors.source_company_id || []"
                    :clearable="!canEditSupplierHeader"
                    @change="clearHeaderValidationError('source_company_id')"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.deal_id"
                    :items="dealOptions"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.PURCHASE_ORDERS.DEAL')"
                    :disabled="!canSave"
                    clearable
                    @change="onDealChange"
                  ></v-autocomplete>
                  <StockDealReference
                    :deal-id="document.deal_id"
                    :deal="document.deal || selectedDealOption"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="2">
                  <DatePicker
                    v-model="document.action_date"
                    :label="$t('STOCK.RECEIPTS.DATE') + '*'"
                    :disabled="!canSave || document.is_finalized_editable === true"
                    without-hint
                  ></DatePicker>
                </v-col>
                <v-col v-if="isVirtualReceipt" cols="12" md="2">
                  <DatePicker
                    v-model="document.available_from"
                    :label="$t('STOCK.RECEIPTS.AVAILABLE_FROM')"
                    :hint="$t('STOCK.RECEIPTS.AVAILABLE_FROM_HINT')"
                    persistent-hint
                    :disabled="!canSave"
                    clearable
                  ></DatePicker>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model.number="document.default_vat_rate"
                    type="number"
                    suffix="%"
                    :label="$t('STOCK.PURCHASE_ORDERS.DEFAULT_VAT')"
                    :disabled="!canSave"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="document.supplier_invoice_no"
                    :label="$t('STOCK.RECEIPTS.SUPPLIER_INVOICE_NO')"
                    :disabled="!canEditSupplierFields"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="2">
                  <DatePicker
                    v-model="document.supplier_invoice_date"
                    :label="$t('STOCK.RECEIPTS.SUPPLIER_INVOICE_DATE')"
                    :disabled="!canEditSupplierFields"
                    without-hint
                    clearable
                  ></DatePicker>
                </v-col>
                <StockIncotermFields
                  :incoterm-id.sync="document.incoterm_id"
                  :incoterm-named-place.sync="document.incoterm_named_place"
                  :incoterm-options="incotermOptions"
                  :incoterm-disabled="incotermFieldsDisabled"
                />
              </v-row>

              <v-alert type="info" dense text class="mb-4">
                <template v-if="showReceiptInventoryCost">
                  {{ $t("STOCK.COMMON.TOTALS_RECEIPT_INVENTORY", {
                    net: formatNum(document.total_net),
                    inventoryCost: formatNum(document.total_inventory_cost_net),
                    vat: formatNum(document.total_vat),
                    gross: formatNum(document.total_gross),
                  }) }}
                </template>
                <template v-else>
                  {{ $t("STOCK.PURCHASE_ORDERS.TOTALS_SUMMARY", {
                    net: formatNum(previewDocumentTotals.total_net),
                    vat: formatNum(previewDocumentTotals.total_vat),
                    gross: formatNum(previewDocumentTotals.total_gross),
                  }) }}
                </template>
              </v-alert>

              <v-expansion-panels v-if="!isVirtualReceipt" v-model="extraCostsPanel" flat class="mb-4">
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <span class="font-weight-medium">{{ $t("STOCK.RECEIPTS.EXTRA_COSTS") }}</span>
                    <span v-if="extraCosts.length" class="text-caption grey--text ml-2">
                      ({{ extraCosts.length }})
                    </span>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-alert type="info" dense text class="mb-3">
                      {{ $t("STOCK.RECEIPTS.EXTRA_COSTS_HINT") }}
                    </v-alert>
                    <v-simple-table dense class="stock-extra-costs-table">
                      <thead>
                        <tr>
                          <th>{{ $t("STOCK.RECEIPTS.EXTRA_CATEGORY") }}</th>
                          <th>{{ $t("GENERAL.LABELS.DESCRIPTION") }}</th>
                          <th>{{ $t("STOCK.RECEIPTS.AMOUNT_NET") }}</th>
                          <th>{{ $t("STOCK.PURCHASE_ORDERS.VAT") }}</th>
                          <th>{{ $t("STOCK.RECEIPTS.ALLOCATION") }}</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(extra, extraIndex) in extraCosts" :key="extra._key">
                          <td>
                            <v-select
                              v-model="extra.extra_cost_type_id"
                              :items="extraCostTypeOptions"
                              item-text="label"
                              item-value="id"
                              dense
                              hide-details
                              :disabled="!canSave"
                            ></v-select>
                          </td>
                          <td>
                            <v-text-field
                              v-model="extra.description"
                              dense
                              hide-details
                              :disabled="!canSave"
                            ></v-text-field>
                          </td>
                          <td>
                            <v-text-field
                              v-model.number="extra.amount_net"
                              type="number"
                              min="0"
                              step="0.01"
                              dense
                              hide-details
                              :class="extraAmountNetFieldClass(extra)"
                              :disabled="!canSave"
                              @focus="rememberExtraAmountNet(extra)"
                              @input="onExtraAmountNetChange(extra)"
                            ></v-text-field>
                          </td>
                          <td>
                            <v-text-field
                              v-model.number="extra.vat_rate"
                              type="number"
                              suffix="%"
                              dense
                              hide-details
                              class="stock-extra-costs-table__vat"
                              :disabled="!canSave"
                            ></v-text-field>
                          </td>
                          <td>
                            <v-select
                              v-model="extra.allocation_method_id"
                              :items="allocationMethodOptions"
                              item-text="label"
                              item-value="id"
                              dense
                              hide-details
                              :disabled="!canSave"
                              @change="onExtraAllocationMethodChange(extra)"
                            ></v-select>
                            <div
                              v-if="Number(extra.amount_net || 0) > 0"
                              class="text-caption mt-1"
                              :class="extraAllocationStatusClass(extra)"
                            >
                              {{
                                $t("STOCK.RECEIPTS.ALLOCATED_OF", {
                                  allocated: formatNum(extraAllocatedTotal(extra)),
                                  total: formatNum(extra.amount_net),
                                })
                              }}
                              <template v-if="isManualExtraCost(extra)">
                                <span
                                  v-if="manualAllocationIsUnderTotal(extra)"
                                  class="ml-1 warning--text"
                                >
                                  ({{
                                    $t("STOCK.RECEIPTS.EXTRA_ALLOC_REMAINING", {
                                      remaining: formatNum(manualAllocRemaining(extra)),
                                    })
                                  }})
                                </span>
                                <span
                                  v-if="manualAllocationIsOverTotal(extra)"
                                  class="ml-1 error--text"
                                >
                                  ({{ $t("STOCK.RECEIPTS.EXTRA_ALLOC_OVER_TOTAL") }})
                                </span>
                              </template>
                            </div>
                          </td>
                          <td>
                            <v-btn
                              v-if="canSave"
                              icon
                              small
                              @click="removeExtraCost(extraIndex)"
                            >
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                    <v-btn v-if="canSave" class="mt-3 mr-2" small @click="addExtraCost">
                      <v-icon left small>mdi-plus</v-icon>
                      {{ $t("STOCK.RECEIPTS.ADD_EXTRA_COST") }}
                    </v-btn>
                    <v-btn
                      v-if="canImportTransportCost"
                      class="mt-3"
                      small
                      color="primary"
                      outlined
                      :loading="importingTransportCost"
                      @click="importTransportCost"
                    >
                      <v-icon left small>mdi-truck-cargo-container</v-icon>
                      {{ $t("STOCK.RECEIPTS.IMPORT_TRANSPORT_COST") }}
                    </v-btn>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>

              <v-subheader class="px-0">{{ $t("STOCK.PURCHASE_ORDERS.LINES") }}</v-subheader>

              <v-alert v-if="linesLocked" type="info" dense text class="mb-3">
                {{ $t("STOCK.RECEIPTS.LINES_LOCKED_HINT") }}
              </v-alert>

              <v-alert v-if="!lines.length" type="info" dense text class="mb-3">
                {{ $t("STOCK.PRODUCT_FINDER.EMPTY_LINES") }}
              </v-alert>
              <div v-else class="stock-document-lines-wrap">
                <v-simple-table dense class="stock-document-lines stock-document-lines--compact">
                  <thead>
                    <tr>
                      <th
                        v-for="col in lineTableColumns"
                        :key="`hdr-${col.key}`"
                        :class="col.thClass"
                      >
                        {{ col.label }}
                        <div
                          v-if="col.hint"
                          class="text-caption grey--text font-weight-regular"
                        >
                          {{ col.hint }}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(line, index) in lines">
                      <tr
                        :key="`line-${index}-${line.stock_product_id}`"
                        :class="{
                          'stock-document-lines__row--expanded': line._detailsOpen,
                          'stock-document-lines__row--blocked': line.quality_status === 'blocked',
                        }"
                      >
                        <td
                          class="stock-document-lines__sticky stock-document-lines__sticky--index"
                        >
                          {{ index + 1 }}
                        </td>
                        <td
                          class="stock-document-lines__sticky stock-document-lines__sticky--product stock-document-lines__product"
                        >
                          <div class="font-weight-medium">{{ lineProductLabel(line) }}</div>
                          <div class="text-caption grey--text">{{ lineProductMeta(line) }}</div>
                          <div
                            v-if="line.stock_product_id && lineExtraCosts.length"
                            class="stock-document-lines__product-extras"
                          >
                            <div
                              v-for="extra in lineExtraCosts"
                              :key="`extra-${index}-${extra._key}`"
                              class="stock-document-lines__product-extra"
                            >
                              <span
                                class="stock-document-lines__product-extra-label"
                                :title="extraCostColumnSubtitle(extra)"
                              >
                                {{ extraCostColumnTitle(extra) }}
                              </span>
                              <StockLineField
                                v-if="isManualExtraCost(extra)"
                                :key="`manual-${extraAllocFieldKey(line, extra)}`"
                                :value="getManualAllocationAmount(line, extra)"
                                type="number"
                                min="0"
                                step="0.01"
                                alloc
                                :placeholder="manualAllocPlaceholder(line, extra)"
                                :disabled="!canEditExtraLineAllocation(extra)"
                                @focus="focusSelectAllInput"
                                @input="setManualAllocationAmount(line, extra, $event)"
                                @blur="finalizeManualAllocationAmount(line, extra)"
                              />
                              <span
                                v-else
                                class="stock-document-lines__product-extra-amount"
                              >
                                {{ formatLineMoney(extraLineDisplayValue(line, extra)) }}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td class="stock-document-lines__col-qty">
                          <StockLineSyncedQtyFields
                            :line="line"
                            :disabled="!canEditLineStructure"
                            :kg-hint="lineUnitCode(line) || 'kg'"
                            @focus="focusSelectAllInput"
                            @quantity-changed="onLineQtyOrUnitInput(line, lineQuantityKey)"
                          />
                          <StockLineField
                            v-model.number="line.gross_weight_kg"
                            type="number"
                            class="mt-1"
                            :hint="$t('STOCK.COMMON.GROSS_WEIGHT_KG')"
                            :disabled="!canEditLineStructure"
                            @focus="focusSelectAllInput"
                          />
                        </td>
                        <td class="stock-document-lines__col-money">
                          <StockLineField
                            v-model.number="line.unit_cost_net"
                            type="number"
                            :hint="$t('STOCK.RECEIPTS.UNIT_COST_SHORT')"
                            :disabled="!canSave"
                            @focus="focusSelectAllInput"
                            @input="onLineQtyOrUnitInput(line, lineQuantityKey)"
                          />
                          <StockLineField
                            v-model.number="line.line_total_net"
                            type="number"
                            class="mt-1"
                            :hint="$t('STOCK.RECEIPTS.LINE_TOTAL_SHORT')"
                            :disabled="!canSave"
                            @focus="focusSelectAllInput"
                            @input="onLineTotalNetInput(line, lineQuantityKey)"
                          />
                        </td>
                        <td class="stock-document-lines__col-lot">
                          <StockLineField
                            v-model="line.lot_number"
                            :disabled="linesLocked || (!canSave && !canFinalize)"
                            @focus="focusSelectAllInput"
                          />
                        </td>
                        <td class="stock-document-lines__col-quality">
                          <v-select
                            v-model="line.quality_status"
                            :items="qualityStatusOptions"
                            item-value="value"
                            item-text="text"
                            dense
                            hide-details
                            :disabled="linesLocked || (!canSave && !canFinalize)"
                            @change="onQualityStatusChange(line)"
                          />
                        </td>
                        <td class="stock-document-lines__actions text-nowrap">
                          <v-btn
                            icon
                            small
                            :title="
                              line._detailsOpen
                                ? $t('STOCK.RECEIPTS.HIDE_DETAILS')
                                : $t('STOCK.RECEIPTS.MORE_DETAILS')
                            "
                            @click="toggleLineDetails(line)"
                          >
                            <v-icon small>
                              {{ line._detailsOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}
                            </v-icon>
                          </v-btn>
                          <v-btn
                            v-if="canEditLineStructure && line.stock_product_id"
                            icon
                            small
                            :title="$t('STOCK.RECEIPTS.DUPLICATE_PRODUCT_LINE')"
                            @click="duplicateProductLine(index)"
                          >
                            <v-icon small>mdi-plus</v-icon>
                          </v-btn>
                          <v-btn
                            v-if="canEditLineStructure"
                            icon
                            small
                            @click="removeLine(index)"
                          >
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                        </td>
                      </tr>
                      <tr
                        v-if="line._detailsOpen"
                        :key="`line-detail-${index}-${line.stock_product_id}`"
                        class="stock-document-lines__detail"
                      >
                        <td colspan="7">
                          <div class="stock-document-lines__detail-grid">
                            <div>
                              <div class="stock-document-lines__detail-label">
                                {{ $t("STOCK.RECEIPTS.EXTERNAL_LOT") }}
                              </div>
                              <StockLineField
                                v-model="line.external_lot_number"
                                :disabled="linesLocked || (!canSave && !canFinalize)"
                                @focus="focusSelectAllInput"
                              />
                            </div>
                            <div>
                              <div class="stock-document-lines__detail-label">
                                {{ $t("STOCK.INVENTORY.PRODUCTION_DATE") }}
                              </div>
                              <StockLineField
                                v-model="line.production_date"
                                type="date"
                                :disabled="!canEditLineStructure"
                                @focus="focusSelectAllInput"
                              />
                            </div>
                            <div>
                              <div class="stock-document-lines__detail-label">
                                {{ $t("STOCK.RECEIPTS.EXPIRY") }}
                              </div>
                              <StockLineField
                                v-model="line.expiry_date"
                                type="date"
                                :disabled="!canEditLineStructure"
                                @focus="focusSelectAllInput"
                              />
                            </div>
                            <div>
                              <div class="stock-document-lines__detail-label">
                                {{ $t("STOCK.PURCHASE_ORDERS.VAT") }}
                              </div>
                              <StockLineField
                                v-model.number="line.vat_rate"
                                type="number"
                                hint="%"
                                narrow
                                :disabled="!canSave"
                                @focus="focusSelectAllInput"
                                @input="onLineVatRateInput"
                              />
                            </div>
                            <div>
                              <div class="stock-document-lines__detail-label">
                                {{ $t("STOCK.RECEIPTS.QUALITY_HOLD_REASON") }}
                              </div>
                              <StockLineField
                                v-model="line.quality_hold_reason"
                                :disabled="
                                  linesLocked
                                    || line.quality_status !== 'blocked'
                                    || (!canSave && !canFinalize)
                                "
                                @focus="focusSelectAllInput"
                              />
                            </div>
                            <div v-if="showReceiptInventoryCost">
                              <div class="stock-document-lines__detail-label">
                                {{ moneyColumnHeader("STOCK.COMMON.EFFECTIVE_UNIT_COST") }}
                              </div>
                              <div class="stock-document-lines__readonly-money">
                                {{ formatLineMoney(line.effective_unit_cost) }}
                              </div>
                            </div>
                            <div v-if="showReceiptInventoryCost">
                              <div class="stock-document-lines__detail-label">
                                {{ moneyColumnHeader("STOCK.COMMON.INVENTORY_COST_NET") }}
                              </div>
                              <div class="stock-document-lines__readonly-money">
                                {{ formatLineMoney(line.inventory_cost_net) }}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr
                        v-if="index < lines.length - 1"
                        :key="`line-header-${index}-${line.stock_product_id}`"
                        class="stock-document-lines__repeat-header"
                      >
                        <th
                          v-for="col in lineTableColumns"
                          :key="`repeat-${index}-${col.key}`"
                          :class="col.thClass"
                        >
                          {{ col.label }}
                          <div
                            v-if="col.hint"
                            class="text-caption grey--text font-weight-regular"
                          >
                            {{ col.hint }}
                          </div>
                        </th>
                      </tr>
                    </template>
                  </tbody>
                </v-simple-table>
              </div>

              <StockProductLineFinder
                :disabled="!canEditLineStructure"
                @add="addProductFromFinder"
              />

              <v-subheader v-if="statusTimeline.length" class="px-0 mt-4">
                {{ $t("STOCK.RECEIPTS.STATUS_TIMELINE") }}
              </v-subheader>
              <v-timeline v-if="statusTimeline.length" dense class="receipt-status-timeline">
                <v-timeline-item
                  v-for="entry in statusTimeline"
                  :key="entry.id"
                  small
                  :color="timelineEntryColor(entry)"
                >
                  <div class="subtitle-2">
                    {{ timelineEntryLabel(entry) }}
                  </div>
                  <div class="caption grey--text">
                    {{ formatTimelineDate(entry.changed_at) }}
                    <span v-if="entry.changed_by_user && entry.changed_by_user.name">
                      · {{ entry.changed_by_user.name }}
                    </span>
                  </div>
                  <div v-if="entry.notes" class="caption">{{ entry.notes }}</div>
                </v-timeline-item>
              </v-timeline>

              <StockDocumentChainLinks
                v-if="!isNew"
                show-transport-orders
                show-purchase-orders
                show-invoices
                :source-transport-order="document.source_transport_order"
                :purchase-orders="linkedPurchaseOrders"
                :invoices="document.invoices || []"
              />

              <StockDocumentFilesPanel
                v-if="!isNew"
                document-kind="receipt"
                :document-id="receiptId"
                :files="documentFiles"
                :can-regenerate="canRegenerateDocument"
                @files-updated="documentFiles = $event"
              />
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
    <TPConfirm
      v-model="showReverseConfirm"
      :content="$t('STOCK.RECEIPTS.CONFIRM_REVERSE')"
      :confirm-label="$t('STOCK.RECEIPTS.REVERSE')"
      @confirm="executeReverse"
    ></TPConfirm>

    <v-dialog v-model="showConvertToPhysicalDialog" max-width="480">
      <v-card>
        <v-card-title>{{ $t("STOCK.RECEIPTS.CONVERT_TO_PHYSICAL") }}</v-card-title>
        <v-card-text>
          <p class="mb-4">{{ $t("STOCK.RECEIPTS.CONVERT_TO_PHYSICAL_HINT") }}</p>
          <v-autocomplete
            v-model="convertPhysicalWarehouseId"
            :items="convertPhysicalWarehouseOptions"
            item-text="label"
            item-value="id"
            :label="$t('STOCK.RECEIPTS.PHYSICAL_DESTINATION_WAREHOUSE')"
            dense
            outlined
            hide-details="auto"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showConvertToPhysicalDialog = false">
            {{ $t("GENERAL.LABELS.CANCEL") }}
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!convertPhysicalWarehouseId"
            :loading="isProcessing"
            @click="convertToPhysical"
          >
            {{ $t("STOCK.RECEIPTS.CONVERT_TO_PHYSICAL") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <StockInvoiceLinkDialog
      v-model="showInvoiceLinkDialog"
      :action-id="receiptId"
      kind="receipt"
      :title="$t('STOCK.INVOICES.CREATE_FROM_RECEIPT')"
      :hint="$t('STOCK.INVOICES.CREATE_FROM_RECEIPT_HINT', { code: document.code })"
      :submitting="isProcessing"
      @confirm="handleInvoiceLinkConfirm"
      @error="handleInvoiceLinkError"
    />
  </div>
</template>

<script>
import { debounce } from "lodash";
import TPConfirm from "@/views/partials/dialogs/Confirm.vue";
import StockProductLineFinder from "@/views/partials/stock/StockProductLineFinder.vue";
import StockDocumentFilesPanel from "@/views/partials/stock/StockDocumentFilesPanel.vue";
import StockDocumentChainLinks from "@/views/partials/stock/StockDocumentChainLinks.vue";
import StockDocumentPageHeading from "@/views/partials/stock/StockDocumentPageHeading.vue";
import StockDealReference from "@/views/partials/stock/StockDealReference.vue";
import StockIncotermFields from "@/views/partials/stock/StockIncotermFields.vue";
import StockLineField from "@/views/partials/stock/StockLineField.vue";
import StockLineSyncedQtyFields from "@/views/partials/stock/StockLineSyncedQtyFields.vue";
import DatePicker from "@/views/partials/form/DatePicker.vue";
import StockInvoiceLinkDialog from "@/views/partials/stock/invoices/StockInvoiceLinkDialog.vue";
import stockDocumentLinesMixin from "@/views/partials/stock/stockDocumentLines.mixin";
import stockDocumentOptionsMixin from "@/views/partials/stock/stockDocumentOptions.mixin";
import stockDocumentTotalsMixin from "@/views/partials/stock/stockDocumentTotals.mixin";
import stockExtraCostAllocationsMixin from "@/views/partials/stock/stockExtraCostAllocations.mixin";
import stockDocumentContextMixin from "@/views/partials/stock/stockDocumentContext.mixin";
import stockIncotermMixin from "@/views/partials/stock/stockIncoterm.mixin";
import stockStatusTimelineMixin from "@/views/partials/stock/stockStatusTimeline.mixin";
import { GET_COMPANIES_OPTIONS } from "@/store/companies.module";
import { GET_STOCK_WAREHOUSE_OPTIONS } from "@/store/stock/stockWarehouses.module";
import { SHOW_SNACK } from "@/store/snack.module";
import { GET_STOCK_PURCHASE_ORDER } from "@/store/stock/stockPurchaseOrders.module";
import {
  GET_STOCK_RECEIPT,
  CREATE_STOCK_RECEIPT,
  UPDATE_STOCK_RECEIPT,
  FINALIZE_STOCK_RECEIPT,
  DISCARD_STOCK_RECEIPT,
  GET_STOCK_RECEIPT_DRAFT_FROM_PO,
  GET_STOCK_RECEIPT_DRAFT_FROM_TO,
  REVERSE_STOCK_RECEIPT,
  CONVERT_STOCK_RECEIPT_TO_PHYSICAL,
  GET_STOCK_DEAL_OPTIONS,
  GET_STOCK_EXTRA_COST_TYPE_OPTIONS,
  GET_STOCK_EXTRA_COST_ALLOCATION_METHOD_OPTIONS,
  IMPORT_STOCK_RECEIPT_TRANSPORT_COST,
} from "@/store/stock/stockReceipts.module";
import { CREATE_STOCK_INVOICE_FROM_RECEIPT } from "@/store/stock/stockInvoices.module";

const emptyLine = (vat) => ({
  stock_product_id: null,
  quantity: 1,
  secondary_quantity: null,
  packaging_quantities: {},
  unit_id: null,
  unit_code: null,
  unit_cost_net: 0,
  line_total_net: 0,
  gross_weight_kg: null,
  gross_to_net_ratio: null,
  vat_rate: vat,
  lot_number: "",
  external_lot_number: "",
  expiry_date: null,
  production_date: null,
  quality_status: "free",
  quality_hold_reason: "",
  extra_allocations: {},
});

export default {
  name: "StockViewReceipt",
  components: {
    StockProductLineFinder,
    StockLineField,
    StockLineSyncedQtyFields,
    TPConfirm,
    StockDocumentFilesPanel,
    StockDocumentChainLinks,
    StockDocumentPageHeading,
    StockDealReference,
    StockIncotermFields,
    DatePicker,
    StockInvoiceLinkDialog,
  },
  mixins: [
    stockDocumentLinesMixin,
    stockDocumentOptionsMixin,
    stockDocumentTotalsMixin,
    stockExtraCostAllocationsMixin,
    stockDocumentContextMixin,
    stockIncotermMixin,
    stockStatusTimelineMixin,
  ],
  data() {
    return {
      statusTimelineI18nPrefix: "STOCK.STATUSES.RECEIPT",
      lineQuantityKey: "quantity",
      tab: "main",
      extraCostsPanel: 0,
      isProcessing: false,
      importingTransportCost: false,
      showReverseConfirm: false,
      showConvertToPhysicalDialog: false,
      showInvoiceLinkDialog: false,
      convertPhysicalWarehouseId: null,
      convertPhysicalWarehouseOptions: [],
      document: this.emptyDocument(),
      lines: [],
      extraCosts: [],
      extraCostTypeOptions: [],
      allocationMethodOptions: [],
      organizations: [],
      companyOptions: [],
      warehouseOptions: [],
      dealOptions: [],
      companySearch: null,
      validationErrors: {},
      documentFiles: [],
      draftMeta: {},
    };
  },
  computed: {
    receiptId() {
      const id = this.$route.params.id;
      return id ? Number(id) : null;
    },
    isNew() {
      return !this.$route.params.id;
    },
    productLines() {
      return (this.lines || []).filter((l) => l.stock_product_id);
    },
    linkedPurchaseOrders() {
      return this.document.purchase_orders || [];
    },
    incotermFieldsDisabled() {
      if (!this.canSave) {
        return true;
      }
      if (this.document.incoterm_locked) {
        return true;
      }
      return this.linkedPurchaseOrders.length > 0
        || this.lines.some((line) => line.purchase_order_item_id);
    },
    canSave() {
      if (this.isReadOnlyDocument) {
        return false;
      }
      return this.isNew
        ? this.hasPermissions("stock-receipts-create")
        : this.hasPermissions("stock-receipts-edit");
    },
    canEditSupplierHeader() {
      if (this.isNew || !this.hasPermissions("stock-receipts-edit")) {
        return false;
      }
      if (this.document.is_supplier_header_editable === true) {
        return true;
      }
      const slug = this.document.action_type && this.document.action_type.slug;
      return (
        this.document.status === "finalized" &&
        ["receipt_supplier", "receipt_virtual"].includes(slug) &&
        !this.document.is_on_issued_invoice
      );
    },
    canEditSupplierFields() {
      return this.canSave || this.canEditSupplierHeader;
    },
    linesLocked() {
      return Boolean(this.document.lines_locked);
    },
    canEditLineStructure() {
      return this.canSave && !this.linesLocked;
    },
    canImportTransportCost() {
      return this.canSave && !this.isNew && !this.hasTransportExtraCost;
    },
    hasTransportExtraCost() {
      const transportType = this.extraCostTypeOptions.find((t) => t.slug === "transport");
      if (!transportType) {
        return false;
      }
      return (this.extraCosts || []).some(
        (extra) => Number(extra.extra_cost_type_id) === Number(transportType.id)
      );
    },
    canFinalize() {
      return (
        !this.isNew &&
        ["draft", "expected"].includes(this.document.status) &&
        this.hasPermissions("stock-receipts-finalize")
      );
    },
    isVirtualReceipt() {
      const slug = this.document.action_type && this.document.action_type.slug;
      if (slug === "receipt_virtual") {
        return true;
      }
      const warehouseId = this.document.destination_warehouse_id;
      const warehouse = (this.warehouseOptions || []).find(
        (item) => Number(item.id) === Number(warehouseId)
      );
      return warehouse && warehouse.type === "virtual";
    },
    isExpectedVirtualReceipt() {
      return this.isVirtualReceipt && this.document.status === "expected";
    },
    lotsOptionalUntilFinalize() {
      return this.isExpectedVirtualReceipt;
    },
    qualityStatusOptions() {
      return [
        { value: "free", text: this.$t("STOCK.RECEIPTS.QUALITY_STATUS_FREE") },
        { value: "blocked", text: this.$t("STOCK.RECEIPTS.QUALITY_STATUS_BLOCKED") },
      ];
    },
    showFinalizeRequiresLotsHint() {
      return (
        this.canFinalize
        && this.isVirtualReceipt
        && (this.lines || []).some((line) => line.stock_product_id && !line.lot_number)
      );
    },
    canSaveAsExpected() {
      return (
        this.canSave
        && this.isVirtualReceipt
        && this.document.status === "draft"
      );
    },
    canReverse() {
      const slug = this.document.action_type && this.document.action_type.slug;
      return (
        !this.isNew &&
        this.document.status === "finalized" &&
        slug === "receipt_supplier" &&
        this.hasPermissions("stock-corrections-create")
      );
    },
    canRegisterSupplierInvoice() {
      const slug = this.document.action_type && this.document.action_type.slug;
      return (
        !this.isNew &&
        this.document.status === "finalized" &&
        ["receipt_supplier", "receipt_virtual"].includes(slug) &&
        !this.document.is_on_issued_invoice &&
        this.hasPermissions("stock-invoices-create")
      );
    },
    invoiceLinkButtonLabel() {
      if (this.document.draft_invoice && this.document.draft_invoice.code) {
        return this.$t("STOCK.INVOICES.OPEN_DRAFT_INVOICE_SHORT", {
          code: this.document.draft_invoice.code,
        });
      }
      return this.$t("STOCK.INVOICES.CREATE_FROM_RECEIPT");
    },
    canConvertToPhysical() {
      const slug = this.document.action_type && this.document.action_type.slug;
      return (
        !this.isNew &&
        this.document.status === "finalized" &&
        slug === "receipt_virtual" &&
        this.hasPermissions("stock-corrections-create")
      );
    },
    isReadOnlyDocument() {
      if (this.isNew) {
        return false;
      }
      const slug = this.document.action_type && this.document.action_type.slug;
      const editableSlugs = ["receipt_supplier", "receipt_virtual"];
      if (this.document.is_finalized_editable === true) {
        return false;
      }
      return !editableSlugs.includes(slug) || !["draft", "expected"].includes(this.document.status);
    },
    canDiscard() {
      if (this.isNew) {
        return true;
      }
      return (
        ["draft", "expected"].includes(this.document.status) &&
        this.hasPermissions("stock-receipts-cancel")
      );
    },
    canRegenerateDocument() {
      return this.document.status === "finalized";
    },
    statusColor() {
      const map = {
        draft: "grey",
        expected: "orange",
        finalized: "success",
        reversed: "purple",
        cancelled: "error",
      };
      return map[this.document.status] || "grey";
    },
    statusLabel() {
      const key = `STOCK.STATUSES.RECEIPT.${(this.document.status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : this.document.status;
    },
    statusTimelineColorMap() {
      return {
        draft: "grey",
        expected: "orange",
        finalized: "success",
        reversed: "purple",
        cancelled: "error",
      };
    },
    showReceiptInventoryCost() {
      if (this.isVirtualReceipt) {
        return false;
      }
      if (!["finalized", "reversed"].includes(this.document.status)) {
        return false;
      }
      return this.document.total_inventory_cost_net != null
        || (this.lines || []).some((line) => line.effective_unit_cost != null);
    },
    lineExtraCosts() {
      if (this.isVirtualReceipt) {
        return [];
      }
      return this.extraCosts || [];
    },
    lineTableColumns() {
      return [
        {
          key: "index",
          thClass: "stock-document-lines__sticky stock-document-lines__sticky--index",
          label: "#",
        },
        {
          key: "product",
          thClass: "stock-document-lines__sticky stock-document-lines__sticky--product",
          label: this.$t("STOCK.PRODUCTS.PRODUCT"),
        },
        {
          key: "qty",
          thClass: "stock-document-lines__col-qty",
          label: this.$t("STOCK.PURCHASE_ORDERS.QTY"),
        },
        {
          key: "cost",
          thClass: "stock-document-lines__col-money",
          label: this.moneyColumnHeader("STOCK.RECEIPTS.COST"),
        },
        {
          key: "lot",
          thClass: "stock-document-lines__col-lot",
          label: this.$t("STOCK.RECEIPTS.LOT_NUMBER"),
          hint: this.isExpectedVirtualReceipt
            ? this.$t("STOCK.RECEIPTS.LOT_OPTIONAL_UNTIL_FINALIZE")
            : "",
        },
        {
          key: "quality",
          thClass: "stock-document-lines__col-quality",
          label: this.$t("STOCK.RECEIPTS.QUALITY_STATUS"),
        },
        {
          key: "actions",
          thClass: "",
          label: "",
        },
      ];
    },
  },
  watch: {
    "$route.params.id"(id, previousId) {
      if (!id || id === previousId) {
        return;
      }
      if (String(this.document.id) === String(id)) {
        return;
      }
      this.loadReceipt(id);
    },
    companySearch: debounce(function () {
      this.loadCompanies();
    }, 300),
    "document.organization_id"() {
      this.runStockHeaderReloadOnOrganizationChange();
    },
  },
  mounted() {
    this.bootstrap();
  },
  methods: {
    onQualityStatusChange(line) {
      if (!line) {
        return;
      }
      if (line.quality_status !== "blocked") {
        line.quality_hold_reason = "";
        return;
      }
      this.$set(line, "_detailsOpen", true);
    },
    toggleLineDetails(line) {
      this.$set(line, "_detailsOpen", !line._detailsOpen);
    },
    emptyDocument() {
      return {
        organization_id: null,
        destination_warehouse_id: null,
        source_company_id: null,
        deal_id: null,
        action_date: new Date().toISOString().slice(0, 10),
        default_vat_rate: 20,
        currency_code: "EUR",
        exchange_rate_to_base: 1,
        incoterm_id: null,
        incoterm_named_place: null,
        incoterm_locked: false,
        lines_locked: false,
        supplier_invoice_no: null,
        supplier_invoice_date: null,
        notes: null,
        status: "draft",
        available_from: null,
        code: null,
        total_net: 0,
        total_vat: 0,
        total_gross: 0,
        purchase_orders: [],
      };
    },
    async bootstrap() {
      this.isProcessing = true;
      try {
        await this.loadOrganizations();
        await Promise.all([
          this.loadExtraCostOptions(),
          this.loadCompanies(),
          this.loadWarehouses(),
          this.loadDeals(),
          this.loadIncoterms(),
        ]);
        if (!this.isNew) {
          await this.loadReceipt(this.$route.params.id);
        } else {
          await this.withSuppressedStockHeaderReload(async () => {
            if (this.hasStockDraftSourceQuery()) {
              await this.loadDraftFromQuery();
            } else {
              this.applyStockContextDefaultsOnNewDocument();
            }
          });
        }
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async loadExtraCostOptions() {
      const [types, methods] = await Promise.all([
        this.$store.dispatch(GET_STOCK_EXTRA_COST_TYPE_OPTIONS),
        this.$store.dispatch(GET_STOCK_EXTRA_COST_ALLOCATION_METHOD_OPTIONS),
      ]);
      this.extraCostTypeOptions = types || [];
      this.allocationMethodOptions = methods || [];
    },
    async loadReceipt(id) {
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(GET_STOCK_RECEIPT, id);
        await this.applyDocument(data);
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    mapExtraCostsFromApi(apiExtras) {
      return (apiExtras || []).map((e) => {
        const extra = {
          _key: this.nextExtraCostKey(),
          id: e.id,
          extra_cost_type_id: e.extra_cost_type_id || e.extra_cost_type?.id,
          allocation_method_id: e.allocation_method_id || e.allocation_method?.id,
          extra_cost_type: e.extra_cost_type,
          allocation_method: e.allocation_method,
          description: e.description,
          amount_net: Number(e.amount_net),
          vat_rate: e.vat_rate,
          line_allocations: e.line_allocations || [],
        };
        return extra;
      });
    },
    applyExtraAllocationsToLines(mappedExtras) {
      const productLines = (this.lines || []).filter((l) => l.stock_product_id);
      productLines.forEach((line, lineIndex) => {
        const allocations = {};
        mappedExtras.forEach((extra) => {
          const row = (extra.line_allocations || []).find(
            (a) => Number(a.line_index) === lineIndex
          );
          if (row) {
            allocations[extra._key] = Number(row.amount_net);
          }
        });
        this.$set(line, "extra_allocations", allocations);
      });
    },
    async applyDocument(data) {
      await this.withSuppressedStockHeaderReload(async () => {
        this.document = {
          ...this.emptyDocument(),
          ...data,
          ...this.mapReceiptDocument(data),
        };
        await this.reloadReceiptHeaderOptions();
        if (this.isNew) {
          this.setHeaderOptionsSeed(data, "syncReceiptHeaderOptions");
        } else {
          this.clearHeaderOptionsSeed();
        }
        this.syncReceiptHeaderOptions(data);
      });
      this.lines = (data.items || []).map((item) => ({
        ...this.mapLineFromApi(item, data, this.lineQuantityKey),
        lot_number: item.lot_number || item.stock_lot?.lot_number || "",
        external_lot_number: item.external_lot_number || "",
        expiry_date: item.expiry_date,
        production_date: item.production_date,
        quality_status: item.quality_status === "blocked" ? "blocked" : "free",
        quality_hold_reason: item.quality_hold_reason || "",
        purchase_order_item_id: item.purchase_order_item_id,
        stock_transport_order_item_id: item.stock_transport_order_item_id,
        stock_transport_order_stop_item_id: item.stock_transport_order_stop_item_id,
        effective_unit_cost: item.effective_unit_cost,
        inventory_cost_net: item.inventory_cost_net,
        gross_weight_kg: item.gross_weight_kg,
        gross_to_net_ratio: item.gross_to_net_ratio,
        weight_basis: item.weight_basis,
        extra_allocations: {},
      }));
      const mappedExtras = this.mapExtraCostsFromApi(data.extra_costs);
      this.extraCosts = mappedExtras;
      this.applyExtraAllocationsToLines(mappedExtras);
      if (mappedExtras.length) {
        this.extraCostsPanel = 0;
      }
      this.documentFiles = Array.isArray(data.files) ? data.files : [];
      this.syncIncotermFromDocument(data);
    },
    payload() {
      if (this.canEditSupplierHeader && !this.canSave) {
        return {
          source_company_id: this.document.source_company_id,
          supplier_invoice_no: this.document.supplier_invoice_no,
          supplier_invoice_date: this.document.supplier_invoice_date,
        };
      }

      this.finalizeAllManualAllocations();
      const params = {
        ...this.document,
        items: this.lines
          .filter((l) => l.stock_product_id)
          .map((line, index) => ({
            line_no: index + 1,
            stock_product_id: line.stock_product_id,
            quantity: line.quantity,
            unit_id: line.unit_id,
            gross_weight_kg: line.gross_weight_kg,
            unit_cost_net: line.unit_cost_net,
            vat_rate: line.vat_rate ?? this.document.default_vat_rate,
            lot_number: line.lot_number,
            external_lot_number: line.external_lot_number,
            expiry_date: line.expiry_date,
            production_date: line.production_date,
            quality_status: line.quality_status === "blocked" ? "blocked" : "free",
            quality_hold_reason:
              line.quality_status === "blocked" ? line.quality_hold_reason || null : null,
            purchase_order_item_id: line.purchase_order_item_id,
            stock_transport_order_item_id: line.stock_transport_order_item_id,
            stock_transport_order_stop_item_id: line.stock_transport_order_stop_item_id,
          })),
        extra_costs: this.buildExtraCostsPayload(
          this.extraCosts,
          this.productLines,
          this.lineQuantityKey
        ),
      };

      if (this.draftMeta.source_transport_order_id) {
        params.source_transport_order_id = this.draftMeta.source_transport_order_id;
      }

      return params;
    },
    async save(options = {}) {
      if (this.canEditSupplierHeader && !this.canSave) {
        if (!this.document.source_company_id) {
          this.validationErrors = {
            ...(this.validationErrors || {}),
            source_company_id: [this.$t("GENERAL.VALIDATION.REQUIRED_FIELD")],
          };
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("STOCK.RECEIPTS.SUPPLIER_REQUIRED_FOR_INVOICE"),
            color: "error",
          });
          return false;
        }
      } else if (!this.validateOrganizationAndWarehouseRequired()) {
        return false;
      }
      this.isProcessing = true;
      try {
        this.validationErrors = {};
        const params = this.payload();
        if (options.statusOverride) {
          params.status = options.statusOverride;
        } else if (this.document.status === "expected") {
          params.status = "expected";
        }
        let data;
        if (this.isNew) {
          data = await this.$store.dispatch(CREATE_STOCK_RECEIPT, params);
          this.$router.replace({
            name: "stock-receipts-view",
            params: { id: data.id },
          });
        } else {
          data = await this.$store.dispatch(UPDATE_STOCK_RECEIPT, {
            id: this.$route.params.id,
            params,
          });
        }
        await this.applyDocument(data);
        if (!options.skipSuccessSnack) {
          const message = params.status === "expected"
            ? this.$t("STOCK.RECEIPTS.SAVE_AS_EXPECTED_MSG")
            : this.$t("GENERAL.SUCCESS_MESSAGE");
          this.$store.dispatch(SHOW_SNACK, {
            message,
            color: "success",
            timeout: params.status === "expected" ? 8000 : undefined,
          });
        }
        return true;
      } catch (errors) {
        this.validationErrors = (errors && errors.errors) || {};
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
        return false;
      } finally {
        this.isProcessing = false;
      }
    },
    async saveAsExpected() {
      await this.save({ statusOverride: "expected" });
    },
    async finalizeReceipt() {
      await this.save();
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(
          FINALIZE_STOCK_RECEIPT,
          this.$route.params.id
        );
        await this.applyDocument(data);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.RECEIPTS.FINALIZED_MSG"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async registerSupplierInvoice(options = {}) {
      this.isProcessing = true;
      try {
        if (this.canEditSupplierHeader) {
          if (!this.document.source_company_id) {
            this.$store.dispatch(SHOW_SNACK, {
              message: this.$t("STOCK.RECEIPTS.SUPPLIER_REQUIRED_FOR_INVOICE"),
              color: "error",
            });
            return;
          }
          const saved = await this.save({ skipSuccessSnack: true });
          if (!saved) {
            return;
          }
        }
        const data = await this.$store.dispatch(CREATE_STOCK_INVOICE_FROM_RECEIPT, {
          receiptId: this.$route.params.id,
          params: options,
        });
        this.showInvoiceLinkDialog = false;
        this.$router.push({ name: "stock-invoices-view", params: { id: data.id } });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    openInvoiceLinkDialog() {
      if (this.document.draft_invoice && this.document.draft_invoice.id) {
        this.$router.push({
          name: "stock-invoices-view",
          params: { id: this.document.draft_invoice.id },
        });
        return;
      }
      if (this.canEditSupplierHeader && !this.document.source_company_id) {
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.RECEIPTS.SUPPLIER_REQUIRED_FOR_INVOICE"),
          color: "error",
        });
        return;
      }
      this.showInvoiceLinkDialog = true;
    },
    handleInvoiceLinkConfirm(options) {
      if (options.openOnly && options.stock_invoice_id) {
        this.showInvoiceLinkDialog = false;
        this.$router.push({
          name: "stock-invoices-view",
          params: { id: options.stock_invoice_id },
        });
        return;
      }
      this.registerSupplierInvoice(options);
    },
    handleInvoiceLinkError(errors) {
      this.$store.dispatch(SHOW_SNACK, {
        message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
        color: "error",
      });
    },
    async importTransportCost() {
      if (!this.$route.params.id) {
        return;
      }

      this.importingTransportCost = true;
      try {
        if (this.canSave) {
          await this.save();
        }
        const data = await this.$store.dispatch(
          IMPORT_STOCK_RECEIPT_TRANSPORT_COST,
          this.$route.params.id
        );
        await this.applyDocument(data);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.RECEIPTS.IMPORT_TRANSPORT_COST_SUCCESS"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.importingTransportCost = false;
      }
    },
    async executeReverse() {
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(
          REVERSE_STOCK_RECEIPT,
          this.$route.params.id
        );
        this.showReverseConfirm = false;
        await this.applyDocument(data);
        if (String(this.$route.params.id) !== String(data.id)) {
          await this.$router.push({
            name: "stock-receipts-view",
            params: { id: data.id },
          });
        }
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async openConvertToPhysicalDialog() {
      this.convertPhysicalWarehouseId = null;
      this.convertPhysicalWarehouseOptions = [];
      this.showConvertToPhysicalDialog = true;

      try {
        const defaultWarehouse = await this.resolveDefaultPhysicalWarehouse();

        const data = await this.$store.dispatch(GET_STOCK_WAREHOUSE_OPTIONS, {
          organization_ids: this.organizationIdsForWarehouseQuery(),
        });
        const warehouses = data.data || data;
        this.convertPhysicalWarehouseOptions = this.mapPhysicalWarehouseOptions(warehouses);

        const defaultWarehouseId = defaultWarehouse?.id || null;
        if (defaultWarehouseId) {
          let option = this.convertPhysicalWarehouseOptions.find(
            (warehouse) => Number(warehouse.id) === Number(defaultWarehouseId)
          );
          if (!option && defaultWarehouse?.warehouse?.type === "physical") {
            this.convertPhysicalWarehouseOptions = this.mergeOptionIntoList(
              this.convertPhysicalWarehouseOptions,
              {
                ...defaultWarehouse.warehouse,
                label: this.warehouseOptionLabel(defaultWarehouse.warehouse),
              }
            );
            option = this.convertPhysicalWarehouseOptions.find(
              (warehouse) => Number(warehouse.id) === Number(defaultWarehouseId)
            );
          }
          if (option) {
            this.convertPhysicalWarehouseId = option.id;
          }
        } else if (this.convertPhysicalWarehouseOptions.length === 1) {
          this.convertPhysicalWarehouseId = this.convertPhysicalWarehouseOptions[0].id;
        }
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      }
    },
    async resolveDefaultPhysicalWarehouse() {
      const fromMeta = this.draftMeta?.physical_destination_warehouse_id
        ? Number(this.draftMeta.physical_destination_warehouse_id)
        : null;

      const linkedPo = (this.document.purchase_orders || [])[0];
      if (!fromMeta && !linkedPo?.id) {
        return null;
      }

      let warehouse = null;
      let id = fromMeta;

      if (linkedPo?.id) {
        try {
          const po = await this.$store.dispatch(GET_STOCK_PURCHASE_ORDER, linkedPo.id);
          if (!id && po?.destination_warehouse_id) {
            id = Number(po.destination_warehouse_id);
          }
          if (po?.destination_warehouse) {
            warehouse = po.destination_warehouse;
          }
        } catch (error) {
          return id ? { id, warehouse: null } : null;
        }
      }

      return id ? { id, warehouse } : null;
    },
    async convertToPhysical() {
      if (!this.convertPhysicalWarehouseId) {
        return;
      }

      this.isProcessing = true;
      try {
        const result = await this.$store.dispatch(
          CONVERT_STOCK_RECEIPT_TO_PHYSICAL,
          {
            id: this.$route.params.id,
            params: {
              destination_warehouse_id: this.convertPhysicalWarehouseId,
            },
          }
        );
        this.showConvertToPhysicalDialog = false;
        await this.applyDocument(result.virtual_receipt);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.RECEIPTS.CONVERT_TO_PHYSICAL_SUCCESS"),
          color: "success",
        });
        if (result.physical_receipt && result.physical_receipt.id) {
          await this.$router.push({
            name: "stock-receipts-view",
            params: { id: result.physical_receipt.id },
          });
        }
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async loadDraftFromQuery() {
      const query = this.$route.query || {};
      const purchaseOrderId = query.purchase_order_id;
      const transportOrderId = query.transport_order_id;

      if (!purchaseOrderId && !transportOrderId) {
        return;
      }

      try {
        const draftParams = {};
        if (query.receipt_flow) {
          draftParams.receipt_flow = query.receipt_flow;
        }
        if (query.stop_id) {
          draftParams.stop_id = query.stop_id;
        }

        const data = purchaseOrderId
          ? await this.$store.dispatch(GET_STOCK_RECEIPT_DRAFT_FROM_PO, {
              id: purchaseOrderId,
              params: draftParams,
            })
          : await this.$store.dispatch(GET_STOCK_RECEIPT_DRAFT_FROM_TO, {
              id: transportOrderId,
              params: draftParams,
            });

        this.draftMeta = data.meta || {};
        await this.applyDocument(data);
      } catch (errors) {
        const message =
          (errors?.errors && Object.values(errors.errors).flat()[0]) ||
          errors?.message ||
          this.$t("GENERAL.UNEXPECTED_ERROR");
        this.$store.dispatch(SHOW_SNACK, {
          message,
          color: "error",
        });
      }
    },
    async discardReceipt() {
      if (this.isNew) {
        this.$router.back();
        return;
      }

      if (!confirm(this.$t("STOCK.DOCUMENTS.CONFIRM_DISCARD"))) {
        return;
      }

      this.isProcessing = true;
      try {
        await this.$store.dispatch(DISCARD_STOCK_RECEIPT, this.$route.params.id);
        this.$router.push({ name: "stock-movements" });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    addProductFromFinder(product) {
      this.addProductLine(product, emptyLine);
    },
    duplicateProductLine(index) {
      const line = this.lines[index];
      if (!line?.stock_product_id || !line.stock_product) {
        return;
      }

      const newLine = {
        ...line,
        quantity: 1,
        secondary_quantity: null,
        packaging_quantities: {},
        gross_weight_kg: null,
        extra_allocations: {},
        lot_number: "",
        external_lot_number: "",
        production_date: null,
        expiry_date: null,
        effective_unit_cost: null,
        inventory_cost_net: null,
        _detailsOpen: false,
      };
      delete newLine.id;
      this.initializeLinePackagingQuantities(newLine, line.stock_product);
      newLine.line_total_net = this.computeLineNetFromUnit(newLine, this.lineQuantityKey);

      this.lines.splice(index + 1, 0, newLine);
    },
    removeLine(index) {
      this.lines.splice(index, 1);
    },
    defaultExtraCostTypeId() {
      const transport = this.extraCostTypeOptions.find((t) => t.slug === "transport");
      return transport ? transport.id : this.extraCostTypeOptions[0]?.id;
    },
    defaultAllocationMethodId(slug = "by_value") {
      const method = this.allocationMethodOptions.find((m) => m.slug === slug);
      return method ? method.id : this.allocationMethodOptions[0]?.id;
    },
    addExtraCost() {
      this.extraCosts.push({
        _key: this.nextExtraCostKey(),
        extra_cost_type_id: this.defaultExtraCostTypeId(),
        allocation_method_id: this.defaultAllocationMethodId(),
        description: "",
        amount_net: 0,
        vat_rate: null,
        line_allocations: [],
      });
      this.extraCostsPanel = 0;
    },
    removeExtraCost(index) {
      const extra = this.extraCosts[index];
      if (extra) {
        this.clearExtraAllocationsOnLines(extra._key);
      }
      this.extraCosts.splice(index, 1);
    },
    onExtraAllocationMethodChange(extra) {
      this.clearExtraAllocationsOnLines(extra._key);
    },
    extraAmountNetFieldClass(extra) {
      if (!this.isManualExtraCost(extra)) {
        return "";
      }
      if (this.manualAllocationIsOverTotal(extra)) {
        return "error--text";
      }
      if (this.manualAllocationIsUnderTotal(extra)) {
        return "warning--text";
      }
      return "";
    },
    extraCostTypeLabel(extra) {
      const type = this.extraCostTypeOptions.find(
        (t) => Number(t.id) === Number(extra.extra_cost_type_id)
      );
      return type?.label || type?.name || "";
    },
    extraCostColumnTitle(extra) {
      return this.extraCostTypeLabel(extra) || this.$t("STOCK.RECEIPTS.EXTRA_SERVICE");
    },
    extraCostColumnSubtitle(extra) {
      const method = this.allocationMethodOptions.find(
        (m) => Number(m.id) === Number(extra.allocation_method_id)
      );
      const methodName = method?.label || method?.name || "";
      const amount = this.formatNum(extra.amount_net);
      const ccy = this.document.currency_code || "";
      return [methodName, amount, ccy].filter(Boolean).join(" · ");
    },
    moneyColumnHeader(i18nKey) {
      const label = this.$t(i18nKey);
      const ccy = this.document.currency_code;
      return ccy ? `${label} · ${ccy}` : label;
    },
    formatLineMoney(value) {
      if (value == null || value === "") {
        return "-";
      }
      return this.formatNum(value);
    },
    extraAllocFieldKey(line, extra) {
      const productIndex = this.productLineIndex(line);
      return [
        extra._key,
        extra.allocation_method_id,
        extra.amount_net,
        line.quantity,
        line.unit_cost_net,
        line.line_total_net,
        productIndex,
      ].join("-");
    },
    extraLineDisplayValue(line, extra) {
      return this.previewExtraLineAmount(
        this.productLines,
        extra,
        this.productLineIndex(line),
        this.lineQuantityKey
      );
    },
    productLineIndex(line) {
      return this.productLines.findIndex((l) => l === line);
    },
    extraAllocationStatusClass(extra) {
      const total = Number(extra.amount_net || 0);
      const allocated = this.extraAllocatedTotal(extra);
      if (this.isManualExtraCost(extra)) {
        if (this.manualAllocationIsOverTotal(extra)) {
          return "error--text";
        }
        if (this.manualAllocationIsUnderTotal(extra)) {
          return "warning--text";
        }
      }
      if (total > 0 && Math.abs(allocated - total) < 0.0001) {
        return "success--text";
      }
      return "grey--text";
    },
    async loadCompanies() {
      const data = await this.$store.dispatch(GET_COMPANIES_OPTIONS, {
        search: this.companySearch,
      });
      this.companyOptions = data.data || data;
      this.preserveDealPartyCompaniesInOptions();
    },
    async loadDeals() {
      const responseData = await this.$store.dispatch(GET_STOCK_DEAL_OPTIONS, {
        organization_id: this.document.organization_id,
        limit: 50,
      });
      this.applyDealOptionsResponse(responseData);
    },
    formatNum(v) {
      return Number(v || 0).toFixed(2);
    },
  },
};
</script>

<style scoped>
.stock-view-page {
  padding-bottom: 48px;
  margin-bottom: 24px;
}
.stock-receipt-linked-po a {
  font-weight: 500;
  text-decoration: none;
}
.stock-receipt-linked-po a:hover {
  text-decoration: underline;
}
.stock-document-lines-wrap {
  overflow-x: auto;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
}
.stock-document-lines {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
}
.stock-document-lines th,
.stock-document-lines td {
  vertical-align: top !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}
.stock-document-lines thead th {
  background: #f5f5f5;
  white-space: nowrap;
}
.stock-document-lines__repeat-header th {
  background: #f5f5f5;
  white-space: nowrap;
  font-weight: 500;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  padding-top: 10px !important;
  padding-bottom: 6px !important;
  border-top: 1px solid rgba(0, 0, 0, 0.16);
}
.stock-document-lines__repeat-header .stock-document-lines__sticky {
  background: #f5f5f5;
  z-index: 3;
}
.stock-document-lines__sticky--index {
  position: sticky;
  left: 0;
  z-index: 2;
  width: 44px;
  min-width: 44px;
  max-width: 44px;
  background: #fff;
  text-align: center;
}
.stock-document-lines__sticky--product {
  position: sticky;
  left: 44px;
  z-index: 2;
  min-width: 280px;
  width: 32%;
  max-width: 360px;
  background: #fff;
  box-shadow: 4px 0 8px -4px rgba(0, 0, 0, 0.14);
}
.stock-document-lines thead .stock-document-lines__sticky {
  z-index: 4;
  background: #f5f5f5;
}
.stock-document-lines__product .font-weight-medium {
  line-height: 1.25;
}
.stock-document-lines__product-extras {
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px dashed rgba(0, 0, 0, 0.16);
}
.stock-document-lines__product-extra {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 28px;
}
.stock-document-lines__product-extra + .stock-document-lines__product-extra {
  margin-top: 2px;
}
.stock-document-lines__product-extra-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.stock-document-lines__product-extra-amount {
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.87);
}
.stock-document-lines__product-extra :deep(.stock-line-field) {
  width: 96px;
  flex: 0 0 96px;
}
.stock-document-lines__col-qty {
  min-width: 128px;
  max-width: 160px;
  vertical-align: top;
}
.stock-document-lines__col-money {
  min-width: 140px;
  max-width: 168px;
}
.stock-document-lines__col-lot {
  min-width: 128px;
  max-width: 160px;
}
.stock-document-lines__col-quality {
  min-width: 120px;
  max-width: 148px;
}
.stock-document-lines__actions {
  white-space: nowrap;
  width: 1%;
}
.stock-document-lines__row--expanded .stock-document-lines__sticky,
.stock-document-lines__row--expanded td {
  background: #fafafa;
}
.stock-document-lines__row--blocked .stock-document-lines__col-quality {
  box-shadow: inset 3px 0 0 #fb8c00;
}
.stock-document-lines__detail td {
  background: #fafafa !important;
  padding: 12px 16px 16px !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
.stock-document-lines__detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(168px, 1fr));
  gap: 12px 16px;
  max-width: 960px;
}
.stock-document-lines__detail-label {
  font-size: 11px;
  letter-spacing: 0.02em;
  color: rgba(0, 0, 0, 0.54);
  margin-bottom: 4px;
}
.stock-document-lines__readonly-money {
  font-variant-numeric: tabular-nums;
  line-height: 36px;
}
.stock-document-lines :deep(.stock-line-field__input) {
  width: 100%;
}
.stock-document-lines :deep(.stock-line-field__input .v-input__slot) {
  min-height: 36px !important;
}
.stock-extra-costs-table__vat {
  max-width: 90px;
}
</style>
